import { showNotification } from '@mantine/notifications';
import { useApiBulkUpdateMonitors } from '@repo/api-codegen';
import { useQueryClient } from '@tanstack/react-query';
import { monitorsQueryKeyFactory } from './constants';

export function useBulkUpdateMonitors(
	options?: Parameters<typeof useApiBulkUpdateMonitors>[0]
) {
	const queryClient = useQueryClient();

	return useApiBulkUpdateMonitors({
		...options,
		onSuccess: (data, variables, context) => {
			// Only invalidate on actual success
			if (data.success) {
				queryClient.invalidateQueries({
					queryKey: monitorsQueryKeyFactory.all(),
				});
			}

			// Call the original onSuccess if provided
			options?.onSuccess?.(data, variables, context);
		},
		onError: (error, variables, context) => {
			// The error is a MonitorBulkUpdateOut when endpoint returns a status that is not 'unknown'
			const errorData = error.status !== 'unknown' ? error.payload : null;

			if (errorData && !errorData.success) {
				// Handle structured error response
				const errorMessage =
					errorData.error_message || 'Bulk monitor update failed.';
				const failedIds = errorData.failed_ids || [];

				// Build notification message with failed IDs if available
				let message = errorMessage;
				if (failedIds.length > 0) {
					const idsDisplay =
						failedIds.length <= 5
							? failedIds.join(', ')
							: `${failedIds.slice(0, 5).join(', ')} and ${failedIds.length - 5} more`;
					message = `${errorMessage} (Failed to update monitors with IDs: ${idsDisplay})`;
				}

				showNotification({
					title: 'Bulk update failed',
					message: message,
					color: 'red',
				});
			}

			// Call the original onError if provided
			options?.onError?.(error, variables, context);
		},
	});
}
